// src/hooks/useChatMessages.js
//
// Chat v2: real-time subscription to messages in a single chat,
// ordered oldest -> newest so message bubbles render naturally
// top-to-bottom in the UI.
//
// MVP: loads the most recent 100 messages and listens for new
// ones. Pagination (load-older) gets added later if anyone
// actually builds chat history that deep. Today's beta-tester
// traffic is well under that ceiling.
//
// Replaces the v1 hook of the same name (which queried the
// pre-pivot /chats schema and was wired to ChatWindow.jsx, both
// removed in the chat rebuild).

import { useEffect, useState } from 'react'
import {
  collection, query, orderBy, limit, onSnapshot,
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

const PAGE_SIZE = 100

export default function useChatMessages(chatId) {
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!chatId) {
      setMessages([])
      setLoading(false)
      return
    }
    setLoading(true)

    // We query DESC + limit then reverse client-side. Querying
    // ASC + limit would return the OLDEST 100 messages (useless);
    // we want the NEWEST 100 in chronological order.
    const q = query(
      collection(db, 'chats', chatId, 'messages'),
      orderBy('sentAt', 'desc'),
      limit(PAGE_SIZE),
    )

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        docs.reverse() // oldest first for top-to-bottom rendering
        setMessages(docs)
        setLoading(false)
      },
      (err) => {
        console.warn('[useChatMessages] snapshot error:', err?.code, err?.message)
        setError(err?.message || 'messages-failed')
        setLoading(false)
      },
    )
    return () => unsub()
  }, [chatId])

  return { messages, loading, error }
}
